import { forwardRef, useImperativeHandle, useState } from "react";
import { Box, TextField, Typography, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import apiClient from "../services/apiClient";
import { ImageSelectOptions, ROOT_FOLDER } from "../features/images/image";
import { useDialogs } from "../contexts/useDialogs";
import { NEED_UPGRADE_ERROR } from "../services/errors";

interface UrlImageUploadParams {
  options: ImageSelectOptions;
  onChange: (count: number) => void;
  onUploadFinish: (success: boolean) => void;
  onFileUpload: (path: string, fileSize: number, error?: string) => void;
  minHeight?: string;
}

export type UrlImageUploadRef = {
  upload: () => void;
  reset: () => void;
};

export const UrlImageUpload = forwardRef<
  UrlImageUploadRef,
  UrlImageUploadParams
>(
  (
    {
      onChange,
      onUploadFinish,
      onFileUpload,
      options,
      minHeight,
    }: UrlImageUploadParams,
    ref: React.MutableRefObject<UrlImageUploadRef>
  ) => {
    const [imageUrl, setImageUrl] = useState("");
    const [customFileName, setCustomFileName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const { openUpgradeModal } = useDialogs();

    useImperativeHandle(ref, () => ({
      upload: async () => {
        if (!imageUrl.trim()) {
          setError("Please enter a valid image URL");
          return;
        }

        setUploading(true);
        setError(null);

        try {
          // Prepare request data with optional custom filename
          const requestData: {
            url: string;
            folder?: string;
            fileName?: string;
          } = {
            url: imageUrl,
            folder: options.folder === ROOT_FOLDER ? "" : options.folder,
          };

          // Only include fileName if user provided one
          if (customFileName.trim()) {
            requestData.fileName = customFileName.trim();
          }

          const response = await apiClient.post(
            "/images/upload-from-url",
            requestData
          );

          if (response.data) {
            const uploadedPath = response.data;
            onFileUpload(uploadedPath, 0); // Size unknown for URL uploads
            onUploadFinish(true);
            setImageUrl("");
            enqueueSnackbar("Image uploaded successfully", {
              variant: "success",
            });
          } else {
            throw new Error("Upload failed");
          }
        } catch (err: unknown) {
          console.error("URL upload error:", err);

          const error = err as {
            response?: { data?: string | { message?: string } };
            message?: string;
          };

          if (error.response?.data === NEED_UPGRADE_ERROR) {
            openUpgradeModal({ type: "images", entryPoint: "image-url-upload" });
          } else {
            const errorMessage =
              typeof error.response?.data === "object"
                ? error.response.data.message
                : error.response?.data || error.message || "Upload failed";
            setError(errorMessage);
            enqueueSnackbar(`Upload failed: ${errorMessage}`, {
              variant: "error",
            });
          }
          onUploadFinish(false);
        } finally {
          setUploading(false);
        }
      },
      reset: () => {
        setImageUrl("");
        setError(null);
        onChange(0);
      },
    }));

    const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const url = event.target.value;
      setImageUrl(url);
      setError(null);
      onChange(url.trim() ? 1 : 0);
    };

    // Function to clean URL for display by removing query parameters
    const getCleanUrlForDisplay = (url: string) => {
      try {
        const urlObj = new URL(url);
        return urlObj.origin + urlObj.pathname;
      } catch {
        // If URL is invalid, just remove query string manually
        return url.split("?")[0];
      }
    };

    const isValidUrl = (url: string) => {
      try {
        const urlObj = new URL(url);
        // Check if the path (without query string) ends with an image extension
        const pathname = urlObj.pathname.toLowerCase();
        return (
          pathname.includes(".") &&
          /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(pathname)
        );
      } catch {
        // Fallback: check if the URL (without query string) looks like an image
        const cleanUrl = url.split("?")[0].toLowerCase();
        return (
          cleanUrl.includes(".") &&
          /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(cleanUrl)
        );
      }
    };

    return (
      <Box
        sx={{
          minHeight: minHeight || 150,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Enter the URL of an image to upload it to your library
        </Typography>

        <TextField
          fullWidth
          label="Image URL"
          placeholder="https://example.com/image.jpg"
          value={imageUrl}
          onChange={handleUrlChange}
          disabled={uploading}
          error={!!error}
          helperText={
            error ||
            "Supported formats: JPG, PNG, GIF, BMP, WebP (query parameters allowed)"
          }
          sx={{ mb: 1 }}
        />

        <TextField
          fullWidth
          label="Filename (Optional)"
          placeholder={
            imageUrl
              ? "Leave empty to auto-extract from URL"
              : "Enter custom filename"
          }
          value={customFileName}
          onChange={(e) => setCustomFileName(e.target.value)}
          disabled={uploading || !imageUrl}
          helperText={
            imageUrl && customFileName.trim()
              ? `Will use: ${customFileName.trim()}`
              : imageUrl
              ? "Will auto-extract filename from URL"
              : "Enter URL first"
          }
          sx={{ mb: 2 }}
        />

        {imageUrl && isValidUrl(imageUrl) && (
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              p: 2,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Preview:
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom
              sx={{ wordBreak: "break-all" }}
            >
              {getCleanUrlForDisplay(imageUrl)}
            </Typography>
            <img
              src={imageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                objectFit: "contain",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
              onError={() => setError("Unable to load image from this URL")}
            />
          </Box>
        )}

        {uploading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Uploading image...</Typography>
          </Box>
        )}
      </Box>
    );
  }
);

UrlImageUpload.displayName = "UrlImageUpload";
